import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { useInjectReducer, useInjectSaga } from '../../../utils/redux-injectors';
import { CustomerUser } from '../../../types';
import { customerAuthSaga } from './saga';
import { CustomerAuthState } from './types';

export const CUSTOMER_TOKEN_KEY = 'sportshub_customer_token';

const storedToken = localStorage.getItem(CUSTOMER_TOKEN_KEY);

const initialState: CustomerAuthState = {
  user: null,
  token: storedToken,
  bootstrapping: Boolean(storedToken),
  loading: false,
  error: null,
  errorCode: null,
  registrationSuccess: false,
  registrationEmail: null,
};

const customerAuthSlice = createSlice({
  name: 'customerAuth',
  initialState,
  reducers: {
    loginRequest(state, _action: PayloadAction<{ email: string; password: string }>) {
      state.loading = true;
      state.error = null;
      state.errorCode = null;
    },
    loginSuccess(state, action: PayloadAction<{ user: CustomerUser; token: string }>) {
      state.loading = false;
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.errorCode = null;
      localStorage.setItem(CUSTOMER_TOKEN_KEY, action.payload.token);
    },
    loginFailure(state, action: PayloadAction<{ message: string; code?: string }>) {
      state.loading = false;
      state.error = action.payload.message;
      state.errorCode = action.payload.code ?? null;
    },
    registerRequest(
      state,
      _action: PayloadAction<{ email: string; password: string; name: string; phone?: string }>,
    ) {
      state.loading = true;
      state.error = null;
      state.registrationSuccess = false;
      state.registrationEmail = null;
    },
    registerSuccess(state, action: PayloadAction<{ message: string; email: string }>) {
      state.loading = false;
      state.registrationSuccess = true;
      state.registrationEmail = action.payload.email;
    },
    registerFailure(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },
    logout(state) {
      state.user = null;
      state.token = null;
      state.bootstrapping = false;
      state.error = null;
      state.errorCode = null;
      localStorage.removeItem(CUSTOMER_TOKEN_KEY);
    },
    setUser(state, action: PayloadAction<CustomerUser>) {
      state.user = action.payload;
      state.bootstrapping = false;
    },
    setBootstrapping(state, action: PayloadAction<boolean>) {
      state.bootstrapping = action.payload;
    },
    clearError(state) {
      state.error = null;
      state.errorCode = null;
    },
    resetRegistration(state) {
      state.registrationSuccess = false;
      state.registrationEmail = null;
    },
  },
});

export const customerAuthActions = customerAuthSlice.actions;
export const customerAuthReducer = customerAuthSlice.reducer;
export const initialCustomerAuthState = initialState;

export function useCustomerAuthSlice() {
  useInjectReducer({ key: 'customerAuth', reducer: customerAuthSlice.reducer });
  useInjectSaga({ key: 'customerAuth', saga: customerAuthSaga });
  return { actions: customerAuthSlice.actions };
}
